import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../components/LegalPageShell";

export default function Accessibility() {
  const { t } = useTranslation(["common", "legal"]);

  return (
    <LegalPageShell
      title={t("accessibility", "Accessibility Statement")}
      subtitle={t("accessibilitySubtitle", "Current accessibility practices and known limitations in Orrith web experiences.")}
      effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
    >
      <section>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">{t("atAGlance", "At a Glance")}</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">{t("mobileFirstLayout", "Mobile-First Layout")}</h3>
            <p className="text-body-sm text-gray-600 dark:text-gray-400">{t("mobileFirstLayoutDesc", "Core experiences are designed to work across common phone and desktop viewport sizes.")}</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">{t("keyboardUse", "Keyboard Use")}</h3>
            <p className="text-body-sm text-gray-600 dark:text-gray-400">{t("keyboardUseDesc", "Primary navigation and action controls are intended to be keyboard accessible.")}</p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">{t("continuousImprovement", "Continuous Improvement")}</h3>
            <p className="text-body-sm text-gray-600 dark:text-gray-400">{t("continuousImprovementDesc", "We review barriers and prioritize fixes through support tickets and product iteration.")}</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">{t("commitment", "Commitment")}</h2>
        <p className="legal-prose">{t("commitmentDesc", "Orrith aims to provide an accessible, mobile-friendly, and keyboard-usable experience across core product surfaces. Accessibility improvements are part of ongoing product iteration.")}</p>
      </section>

      <section>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">{t("implementedAccessibility", "Implemented Accessibility Behaviors")}</h2>
        <ul className="legal-list">
          <li>{t("responsiveLayouts", "Responsive layouts for mobile and desktop screen sizes")}</li>
          <li>{t("semanticHeadings", "Semantic headings and structured section hierarchy across core pages")}</li>
          <li>{t("keyboardNavigable", "Keyboard-navigable controls for common actions and dialogs")}</li>
          <li>{t("visibleFocus", "Visible focus and hover states on interactive elements")}</li>
          <li>{t("languageMetadata", "Language metadata updates for document root based on selected language")}</li>
          <li>{t("readableContrast", "Readable contrast patterns in supported light and dark themes")}</li>
        </ul>
      </section>

      <section>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">{t("areasRequiringImprovement", "Areas Requiring Ongoing Improvement")}</h2>
        <ul className="legal-list">
          <li>{t("consistencyAdvancedCards", "Accessibility consistency across all advanced data cards and visual dashboards")}</li>
          <li>{t("coverageThirdParty", "Coverage depth for every third-party interaction path and browser combination")}</li>
          <li>{t("assistiveTechValidation", "Extended assistive technology validation across all user journeys")}</li>
          <li>{t("descriptiveLabels", "Further standardization of descriptive labels for all dynamic content states")}</li>
        </ul>
      </section>

      <section>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">{t("feedbackAndSupport", "Feedback and Support")}</h2>
        <p className="legal-prose mb-3">{t("feedbackDesc", "If you encounter an accessibility barrier, report it through the Support Center. Include the page URL, action attempted, and assistive technology or browser details to help triage quickly.")}</p>
        <ul className="legal-list">
          <li>{t("openSupportTicket", "Open a support ticket in the Help Center")}</li>
          <li>{t("useFeedbackChannels", "Use the in-app feedback and error-report channels where available")}</li>
          <li>{t("includeReproductionSteps", "Include clear reproduction steps so the issue can be validated and fixed")}</li>
        </ul>
        <Link
          to="/support"
          className="inline-flex mt-4 px-3.5 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition"
        >
          {t("supportCenter", "Support Center")}
        </Link>
      </section>
    </LegalPageShell>
  );
}